import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  Check,
  ChevronRight,
  FolderKanban,
  PanelLeft,
  Plus,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCommandState } from "cmdk";
import CreateProjectForm from "@/components/CreateProjectForm";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/utils/cn";
import {
  useProjects,
  validateSelectedProjects,
} from "@/contexts/ProjectContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  useProjectsQuery,
  useProjectsQueryScope,
} from "@/features/projects/hooks/useProjectsQuery";
import { notifyProjectSelectionChanged } from "@/utils/project-selection-notifier";

export type TopBarBreadcrumb = {
  label: ReactNode;
  href?: string;
  current?: boolean;
};

const BreadcrumbTrail = ({ items }: { items: TopBarBreadcrumb[] }) => {
  const explicitCurrentIndex = items.findIndex((item) => item.current);
  const currentIndex =
    explicitCurrentIndex >= 0 ? explicitCurrentIndex : items.length - 1;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex-1 overflow-hidden">
      <ol className="flex min-w-0 items-center gap-1 overflow-hidden text-sm font-medium">
        {items.map((item, index) => {
          const isCurrent = index === currentIndex;

          return (
            <li
              key={`${index}-${String(item.label)}`}
              className="flex min-w-0 items-center gap-1"
            >
              {index > 0 && (
                <ChevronRight
                  data-slot="breadcrumb-separator"
                  className="size-3.5 shrink-0 text-muted-foreground rtl:rotate-180"
                  aria-hidden="true"
                />
              )}
              {item.href && !isCurrent ? (
                <a
                  href={item.href}
                  className="min-w-0 truncate rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {item.label}
                </a>
              ) : (
                <span
                  className={cn(
                    "min-w-0 truncate",
                    !isCurrent && "text-muted-foreground",
                  )}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// Use Command's existing fuzzy-match results, excluding its synthetic option.
// CommandEmpty's total also counts All Projects, even with no real matches.
const ProjectSearchEmpty = ({
  allProjectOptionId,
  message,
}: {
  allProjectOptionId: string;
  message: string;
}) => {
  const hasProjectMatches = useCommandState(
    (state) =>
      !state.search ||
      Array.from(state.filtered.items).some(
        ([id, score]) => id !== allProjectOptionId && score > 0,
      ),
  );
  if (!allProjectOptionId || hasProjectMatches) return null;
  return (
    <div role="status" className="py-6 text-center text-sm">
      {message}
    </div>
  );
};

const ProjectChip = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { currentOrganization } = useOrganization();
  const [open, setOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const { scope } = useProjectsQueryScope();
  const scopeKey = JSON.stringify([scope.principalId, scope.organizationId]);
  const [search, setSearch] = useState({ scopeKey, value: "" });
  const projectTriggerRef = useRef<HTMLButtonElement>(null);
  const projectContentRef = useRef<HTMLDivElement | null>(null);
  const [allProjectOptionId, setAllProjectOptionId] = useState("");
  const registerAllProjectOption = useCallback(
    (node: HTMLSpanElement | null) => {
      if (node?.parentElement?.id) setAllProjectOptionId(node.parentElement.id);
    },
    [],
  );
  const { selectedProjects, toggleProject } = useProjects();
  const {
    data: availableProjects,
    error,
    isFetching,
    isPending,
    refetch,
  } = useProjectsQuery();
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects) as string[],
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () =>
      Array.isArray(availableProjects)
        ? Array.from(
            new Map(
              availableProjects.map((project) => [String(project.id), project]),
            ).values(),
          )
        : [],
    [availableProjects],
  );
  const loading = isPending || isFetching;
  const showSearch =
    new Set(safeAvailableProjects.map((project) => String(project.id))).size >=
    6;
  // Derive an empty query synchronously: old text must never filter a new scope
  // or remain active after its input disappears. Do not touch project selection.
  if (search.scopeKey !== scopeKey || (!showSearch && search.value)) {
    setSearch({ scopeKey, value: "" });
  }
  const searchValue =
    showSearch && search.scopeKey === scopeKey ? search.value : "";
  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch({ scopeKey, value: "" });
  };
  const projectNames = useMemo(
    () =>
      new Map(
        safeAvailableProjects.map((project) => [
          String(project.id),
          project.name,
        ]),
      ),
    [safeAvailableProjects],
  );
  const previousSelectionRef = useRef(safeSelectedProjects);
  const previousSearchVisibleRef = useRef(showSearch);

  useLayoutEffect(() => {
    const searchVisibilityChanged =
      previousSearchVisibleRef.current !== showSearch;
    previousSearchVisibleRef.current = showSearch;
    const content = projectContentRef.current;
    if (
      open &&
      searchVisibilityChanged &&
      content?.isConnected &&
      !content.contains(document.activeElement)
    ) {
      content
        .querySelector<HTMLElement>(
          showSearch ? "[cmdk-input]" : "[data-project-scroll] button",
        )
        ?.focus();
    }
  }, [open, showSearch]);

  useEffect(() => {
    notifyProjectSelectionChanged({
      previousSelectionRef,
      selectedProjects: safeSelectedProjects,
      pathname,
    });
  }, [pathname, safeSelectedProjects]);

  const isAll = safeSelectedProjects.includes("all");

  const openCreateProject = () => {
    changeOpen(false);
    setCreateProjectOpen(true);
  };

  const openManageProjects = () => {
    changeOpen(false);
    void navigate({
      to: "/projects",
    });
  };

  const openOrganizationSettings = () => {
    changeOpen(false);
    void navigate({
      to: "/$",
      params: { _splat: "settings" },
      search: { tab: "organizations" },
    });
  };

  const label = isAll
    ? t("sidebar.projectsCount", { count: safeAvailableProjects.length })
    : safeSelectedProjects.length === 1
      ? (projectNames.get(safeSelectedProjects[0]) ??
        String(safeSelectedProjects[0]))
      : t("sidebar.projectsCount", {
          count: safeSelectedProjects.length,
        });

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          ref={projectTriggerRef}
          type="button"
          className="flex h-6 min-w-0 max-w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 text-sm text-card-foreground"
        >
          <FolderKanban
            className="size-4 shrink-0 opacity-70"
            aria-hidden="true"
          />
          <span className="min-w-0 max-w-[160px] truncate">{label}</span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        onFocusCapture={(event) => {
          projectContentRef.current = event.currentTarget;
        }}
        align="end"
        dir={i18n.dir()}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-0"
      >
        {loading ? (
          <div
            className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground"
            aria-live="polite"
          >
            <Spinner />
            {t("projects.loadingProjects")}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <p className="text-sm text-destructive">
              {t("projects.errorLoadingProjects")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
            >
              {t("common.retry")}
            </Button>
          </div>
        ) : (
          <>
            {showSearch ? (
              <Command
                label={t("projects.searchProjects")}
                className="min-h-0 flex-1"
              >
                <div className="relative shrink-0">
                  <CommandInput
                    aria-label={t("projects.searchProjects")}
                    value={searchValue}
                    onValueChange={(value) => setSearch({ scopeKey, value })}
                    placeholder={t("projects.searchProjects")}
                    className="pe-10"
                  />
                  {searchValue && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute end-1 top-0 h-9"
                      aria-label={`${t("common.reset")} ${t("projects.searchProjects")}`}
                      onClick={(event) => {
                        event.currentTarget
                          .closest('[data-slot="command"]')
                          ?.querySelector<HTMLInputElement>("input")
                          ?.focus();
                        setSearch({ scopeKey, value: "" });
                      }}
                    >
                      {t("common.reset")}
                    </Button>
                  )}
                </div>
                <CommandList data-project-scroll className="min-h-0 flex-1">
                  <ProjectSearchEmpty
                    allProjectOptionId={allProjectOptionId}
                    message={t("projects.noMatchingProjects")}
                  />
                  <CommandGroup forceMount>
                    <CommandItem
                      forceMount
                      value={t("projects.allProjects")}
                      onSelect={() => toggleProject("all")}
                    >
                      <FolderKanban className="size-4" aria-hidden="true" />
                      <span ref={registerAllProjectOption}>
                        {t("projects.allProjects")}
                      </span>
                      {isAll && (
                        <span className="sr-only">{t("common.selected")}</span>
                      )}
                      <Check
                        className={cn(
                          "ms-auto size-4",
                          isAll ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden="true"
                      />
                    </CommandItem>
                  </CommandGroup>
                  {/* Keep the group visible on same-ID renames, but override
                      its inherited forceMount below so items stay filtered. */}
                  <CommandGroup forceMount>
                    {safeAvailableProjects.map((project) => {
                      const projectId = String(project.id);
                      const projectName = project.name || projectId;
                      const isSelected =
                        safeSelectedProjects.includes(projectId);

                      return (
                        <CommandItem
                          key={projectId}
                          forceMount={false}
                          value={`${projectName} ${projectId}`}
                          onSelect={() => toggleProject(projectId)}
                        >
                          <FolderKanban className="size-4" aria-hidden="true" />
                          <span className="min-w-0 flex-1 whitespace-normal break-words">
                            {projectName}
                          </span>
                          {isSelected && (
                            <span className="sr-only">
                              {t("common.selected")}
                            </span>
                          )}
                          <Check
                            className={cn(
                              "ms-auto size-4",
                              isSelected ? "opacity-100" : "opacity-0",
                            )}
                            aria-hidden="true"
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            ) : (
              <div
                data-project-scroll
                role="group"
                aria-label={t("projects.allProjects")}
                className="min-h-0 max-h-[300px] overflow-y-auto p-1"
                onKeyDown={(event) => {
                  if (
                    !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
                  )
                    return;
                  const options = Array.from(
                    event.currentTarget.querySelectorAll<HTMLButtonElement>(
                      "button",
                    ),
                  );
                  const index = options.indexOf(
                    document.activeElement as HTMLButtonElement,
                  );
                  const next =
                    event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? options.length - 1
                        : (index +
                            (event.key === "ArrowDown" ? 1 : -1) +
                            options.length) %
                          options.length;
                  event.preventDefault();
                  options[next]?.focus();
                }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  aria-pressed={isAll}
                  className="h-auto min-h-9 w-full justify-start whitespace-normal text-start"
                  onClick={() => toggleProject("all")}
                >
                  <FolderKanban className="size-4" aria-hidden="true" />
                  <span>{t("projects.allProjects")}</span>
                  <Check
                    aria-hidden="true"
                    className={cn(
                      "ms-auto size-4",
                      isAll ? "opacity-100" : "opacity-0",
                    )}
                  />
                </Button>
                {safeAvailableProjects.map((project) => {
                  const id = String(project.id);
                  const selected = safeSelectedProjects.includes(id);
                  return (
                    <Button
                      key={id}
                      type="button"
                      variant="ghost"
                      aria-pressed={selected}
                      className="h-auto min-h-9 w-full justify-start whitespace-normal text-start"
                      onClick={() => toggleProject(id)}
                    >
                      <FolderKanban
                        className="size-4 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 whitespace-normal break-words">
                        {project.name || id}
                      </span>
                      <Check
                        aria-hidden="true"
                        className={cn(
                          "ms-auto size-4 shrink-0",
                          selected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </Button>
                  );
                })}
              </div>
            )}
            <div
              data-project-utilities
              className="shrink-0 space-y-1 border-t border-border p-1"
            >
              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-9 w-full justify-start whitespace-normal text-start"
                onClick={openCreateProject}
              >
                <Plus className="size-4" aria-hidden="true" />
                <span>{t("projects.createProject")}</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-9 w-full justify-start whitespace-normal text-start"
                onClick={openManageProjects}
              >
                <Settings className="size-4" aria-hidden="true" />
                <span>{t("projects.manageProjects")}</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-9 w-full justify-start whitespace-normal text-start"
                onClick={openOrganizationSettings}
              >
                <Building2 className="size-4" aria-hidden="true" />
                <span>{t("settings.organizationsTab")}</span>
                {currentOrganization?.name && (
                  <span className="ms-auto max-w-32 truncate text-xs text-muted-foreground">
                    {currentOrganization.name}
                  </span>
                )}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>

      <CreateProjectForm
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        returnFocusRef={projectTriggerRef}
      />
    </Popover>
  );
};

const OrgChip = () => {
  const { currentOrganization } = useOrganization();
  const name = currentOrganization?.name;
  if (!name) return null;

  return (
    <span className="hidden h-6 min-w-0 items-center gap-2 rounded-md border border-primary/20 bg-primary/10 px-2.5 text-sm text-card-foreground min-[600px]:flex">
      <Building2 className="size-4 shrink-0 opacity-70" aria-hidden="true" />
      <span className="max-w-[160px] truncate">{name}</span>
    </span>
  );
};

export const TopBar = ({
  title,
  breadcrumbs,
  collapsed,
  sidebarControls,
  sidebarTrigger = false,
  onToggleSidebar,
  actions,
}: {
  title?: string;
  breadcrumbs?: TopBarBreadcrumb[];
  collapsed: boolean;
  sidebarControls?: string;
  sidebarTrigger?: boolean;
  onToggleSidebar: () => void;
  actions?: ReactNode;
}) => {
  const { t } = useTranslation();

  const toggleButton = (
    <button
      type="button"
      onClick={onToggleSidebar}
      aria-label={t("sidebar.toggle")}
      aria-expanded={!collapsed}
      {...(sidebarControls ? { "aria-controls": sidebarControls } : {})}
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground",
        "hover:bg-accent hover:text-accent-foreground",
      )}
    >
      <PanelLeft className="size-5" aria-hidden="true" />
    </button>
  );

  return (
    <header className="flex h-[60px] min-w-0 shrink-0 items-center gap-3 border-b border-border px-4">
      {sidebarTrigger ? (
        <SheetTrigger asChild>{toggleButton}</SheetTrigger>
      ) : (
        toggleButton
      )}

      {breadcrumbs && breadcrumbs.length > 0 ? (
        <BreadcrumbTrail items={breadcrumbs} />
      ) : title ? (
        <h1 className="min-w-0 flex-1 truncate text-sm font-medium">{title}</h1>
      ) : null}

      <div className="ms-auto flex min-w-0 shrink items-center gap-2">
        {actions}
        <ProjectChip />
        <OrgChip />
      </div>
    </header>
  );
};
